import React from "react";
import { Link } from "react-router-dom";
import "./About.css";

const principles = [
  {
    title: "Clear records first",
    body:
      "Firearm records, range history, photos, and notes should be easy to manage and useful when owners review them later.",
  },
  {
    title: "Practical over flashy",
    body:
      "A good tool should disappear into the range routine. We focus on fast logging, clear review, and information that helps before the next session.",
  },
  {
    title: "Built for real training",
    body:
      "Inventory, maintenance, range visits, and drills are connected because responsible ownership and skill development happen together.",
  },
];

const productDirection = [
  "Inventory and range logging",
  "Training plans, maintenance, and service reminders",
  "Range planning and training review workflows",
  "Review tools that keep records easier to use",
];

function About() {
  return (
    <main className="about-page">
      <section className="about-hero" aria-labelledby="about-title">
        <div className="about-kicker">ArmorySync LLC</div>
        <h1 id="about-title">Firearm software for people who take ownership seriously.</h1>
        <p className="about-lede">
          ArmorySync builds practical tools for responsible firearm owners first.
          <Link to="/range-pocket">Range Pocket</Link>, available now on iOS
          and Android, helps users organize firearm inventory, range visits,
          maintenance, training plans, and the small details that turn scattered
          notes into useful history.
        </p>
      </section>

      <section className="about-proof" aria-label="Company focus areas">
        <article>
          <span>01</span>
          <strong>Organized owner records</strong>
          <p>Designed so firearm and range history stays clear, organized, and easy to manage.</p>
        </article>
        <article>
          <span>02</span>
          <strong>Range-ready workflows</strong>
          <p>Fast enough for real range days, detailed enough for later review.</p>
        </article>
        <article>
          <span>03</span>
          <strong>Careful product work</strong>
          <p>Every change is evaluated by whether it makes ownership or training easier to manage.</p>
        </article>
      </section>

      <section className="about-product-note" aria-labelledby="product-note-title">
        <div className="about-product-note-copy">
          <div className="about-kicker">Product note</div>
          <h2 id="product-note-title">Range Pocket started with the record problem, not the feature list.</h2>
          <p>
            The app is built around a simple owner routine: know what you have,
            remember what happened at the range, and make the next session
            easier to prepare. That is why the product connects inventory,
            maintenance, photos, range visits, and training instead of treating
            them as separate notebooks.
          </p>
        </div>
        <div className="about-product-note-media" aria-label="Range Pocket app screenshots">
          <img src="/features/feature-armory-status.webp" alt="Range Pocket armory status dashboard" />
          <img src="/features/feature-gunsmith-profile.webp" alt="Range Pocket gunsmith profile screen" />
        </div>
      </section>

      <section className="about-story" aria-labelledby="mission-title">
        <div>
          <div className="about-kicker">Mission</div>
          <h2 id="mission-title">Make responsible ownership easier to manage.</h2>
        </div>
        <div className="about-story-copy">
          <p>
            Firearm ownership creates a lot of practical information: equipment
            details, purchase records, range sessions, round counts, target
            photos, maintenance notes, training plans, and service reminders.
            Most people split that information across paper, spreadsheets,
            photo albums, and memory.
          </p>
          <p>
            We think that history deserves a calmer home. ArmorySync products
            are built to help people prepare better, train with more continuity,
            and keep useful records in a format they can understand and maintain.
          </p>
        </div>
      </section>

      <section className="about-principles" aria-label="What ArmorySync stands for">
        {principles.map((principle) => (
          <article key={principle.title}>
            <h2>{principle.title}</h2>
            <p>{principle.body}</p>
          </article>
        ))}
      </section>

      <section className="about-roadmap" aria-labelledby="roadmap-title">
        <div>
          <div className="about-kicker">Product direction</div>
          <h2 id="roadmap-title">Keep the range workflow connected and easy to review.</h2>
          <p>
            Range Pocket is the starting point: a useful app for records,
            training context, maintenance, and the next range trip. Product
            decisions are measured against whether they make the owner workflow
            clearer.
          </p>
          <Link className="about-cta" to="/products">
            Explore products
          </Link>
        </div>
        <ol>
          {productDirection.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      </section>
    </main>
  );
}

export const About_Page = {
  path: "/about",
  element: <About />,
};

export default About;
